import { useCallback, useEffect, useRef, useState } from 'react';
import { dbSupabase } from '../lib/dbClient';

// Reads clubs from the public `public_clubs` view (see SETUP: a read-only view
// that exposes only marketing-safe fields — no finances). Falls back gracefully
// if the view isn't set up yet. Uses select('*') so that when the view is
// widened with more marketing fields (founded, stadium, motto, honours, etc.)
// they appear automatically without a code change.
//
// Presentation: a full-bleed, INFINITELY LOOPING focus carousel with division
// tabs. Pick a division and the tile nearest the viewport centre is "in focus"
// (scaled up, club-colour glow); its neighbours sit back, smaller and dimmed.
// Each tile has an expand icon that opens a club detail modal. Visitors move
// through the world by dragging, scrolling, tapping a tile, or using the
// prev/next arrows — and it wraps, so the last club sits just behind the first.
//
// The loop is done by rendering three identical copies of the current
// division's clubs and keeping the user parked in the middle copy: whenever the
// focused tile drifts into an outer copy we shift `scrollLeft` by exactly one
// copy-width. Because the copies are pixel-identical, that jump is invisible.

// Division names keyed by tier. Mirrors TIER_NAMES in the editor's clubsData.js;
// the public view only exposes the numeric tier, so we label it here.
const TIER_NAMES = {
  1: 'Premier League',
  2: 'Championship',
  3: 'League One',
  4: 'League Two',
};
const TIER_ORDER = [1, 2, 3, 4];

const COPIES = 3; // one visible middle copy + one clone either side

// A short, fluid identity line per club that merges playing style + ownership
// (with a nod to each club's character), keyed by name so it survives whatever
// row ids the DB uses. Marketing copy, so it lives here rather than in the game
// DB; if a club's style/owner ever changes in the editor, refresh the line.
// Falls back to a plain "style · owner" join for any club not listed.
const IDENTITY_LINES = {
  // Premier League
  'Hartwell City FC': 'Stylish possession football, powered by ambitious ownership.',
  'Caldermoor United': 'A fierce high press under proud local ownership.',
  'Ferndale Athletic': 'Community-owned and lethal on the counter.',
  'Stonebridge Rovers': 'Old-fashioned direct football under a local owner.',
  'Ashbourne FC': 'A patient passing side spending big under new investment.',
  'Millbrook Town': 'Fan-owned and relentless, pressing from the front.',
  'Wrenfield Borough': 'Stubborn counter-attackers under modest local ownership.',
  'Dunsley FC': 'Direct and still dangerous, drifting under an absent owner.',
  'Brackley Vale FC': 'Community-owned and devoted to attractive, patient football.',
  'Thornwick Town': 'Compact counter-punchers backed by a local businessman.',
  'Crestham United': 'An aggressive press driven by ambitious, restless owners.',
  'Holmbury City': 'Direct and fearless, newly promoted under local ownership.',
  // Championship
  'Greenvale United': 'Community-run and comfortable in possession.',
  'Ashworth FC': 'Direct football and fierce local pride under a hometown owner.',
  'Cressford Athletic': 'High-tempo pressing under a driven local owner.',
  'Pelham Town': 'Community-owned counter-attackers biding their time.',
  'Northgate City': 'A fallen giant passing its way back under new investment.',
  'Ravensmoor FC': 'Physical, direct and hard to beat under local ownership.',
  'Holtby Wanderers': 'Fan-owned and ever-adapting on the counter.',
  'Sedgewick Town': 'Direct football and market-town grit under a local owner.',
  'Elmwick Borough': 'Tidy in possession and ambitious under local ownership.',
  'Dunford City': 'A pressing side building fast on fresh investment.',
  'Blackfen Rovers': 'Community-owned, direct and famously hard to beat.',
  'Marefield United': 'Counter-attacking newcomers backed by a local owner.',
  // League One
  'Millhaven Borough': 'Direct football and terrace passion under local ownership.',
  'Thornfield FC': 'Community-owned and quietly building a passing side.',
  'Copsley Town': 'Direct and thrifty, run the right way by a local owner.',
  'Harwick United': 'A relentless press under motivated local ownership.',
  'Fernsby Athletic': 'Fan-owned counter-attackers running on team spirit.',
  'Woldgate Town': 'Community-owned and proudly direct, non-league at heart.',
  'Moorside FC': 'Rugged, direct football under a local owner.',
  'Ridgeley Rovers': 'Community-owned giant-killers on the counter.',
  'Langport City': 'A passing side with big plans under new investment.',
  'Beckford Town': 'Direct and well-drilled under a shrewd local owner.',
  'Dalesbury FC': 'Fan-owned and pressing like their lives depend on it.',
  'Fenwick Athletic': 'Community-owned counter-attackers, small but fearless.',
  // League Two
  'Hareford Town': 'Direct football, adrift under an absent owner.',
  'Penwick FC': 'Volunteer-run and direct, determined to climb.',
  'Brockwell United': 'Counter-attacking hopefuls backed by a local benefactor.',
  'Sladden Town': 'Direct football under a distant, absent owner.',
  'Grantley FC': 'Community-owned and pressing with real energy.',
  'Wyberton Athletic': 'Fan-owned counter-attackers, grassroots made good.',
  'Lowmoor FC': 'Direct football and a proud club under absent ownership.',
  'Tansley Rovers': 'Steady, direct football under patient local ownership.',
  'Kirkby End FC': 'Counter-attacking survivors under an absent owner.',
  'Woolbridge Town': 'Ambitiously passing from the back under a local owner.',
  'Felbeck Wanderers': 'Direct and battling on under an absent owner.',
  'Stainby FC': 'Community-owned and pressing hard, roared on by the loudest support around.',
};

function initials(name) {
  return (name || '')
    .replace(/\bFC\b/gi, '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}

function hexToRgb(hex) {
  const h = (hex || '').trim().replace('#', '');
  const s = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  if (s.length !== 6 || /[^0-9a-f]/i.test(s)) return null;
  const n = parseInt(s, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function rgbToHsl({ r, g, b }) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  let h = 0;
  let s = 0;
  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s, l };
}

function hslToRgb({ h, s, l }) {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0; let g = 0; let b = 0;
  if (h < 60) { r = c; g = x; } else if (h < 120) { r = x; g = c; } else if (h < 180) { g = c; b = x; } else if (h < 240) { g = x; b = c; } else if (h < 300) { r = x; b = c; } else { r = c; b = x; }
  return { r: Math.round((r + m) * 255), g: Math.round((g + m) * 255), b: Math.round((b + m) * 255) };
}

// A version of the club colour that stays visible on the dark UI: floors the
// lightness so dark navies and blacks still read as a coloured border and glow,
// while keeping the club's actual hue. Returns {r,g,b} or null.
function clubColour(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const hsl = rgbToHsl(rgb);
  if (hsl.l < 0.55) hsl.l = 0.55;
  return hslToRgb(hsl);
}

// Themed CSS custom props (border / glow / tint) from a club's primary colour.
function clubVars(hex) {
  const cc = clubColour(hex);
  return {
    border: cc ? `rgb(${cc.r}, ${cc.g}, ${cc.b})` : 'var(--line-strong)',
    glow: cc ? `rgba(${cc.r}, ${cc.g}, ${cc.b}, 0.5)` : 'rgba(200,241,53,0.22)',
    tint: cc ? `rgba(${cc.r}, ${cc.g}, ${cc.b}, 0.16)` : 'rgba(200,241,53,0.10)',
  };
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

// honours may arrive as a JSON array (data->'honours') or, if the view exposes
// it as text, a stringified array. Normalise to an array of strings.
function asHonours(v) {
  if (Array.isArray(v)) return v;
  if (typeof v === 'string' && v.trim()) {
    try {
      const parsed = JSON.parse(v);
      return Array.isArray(parsed) ? parsed : [v];
    } catch (err) {
      return [v];
    }
  }
  return [];
}

// Placeholder crest for clubs that don't have a real badge yet: a shield in the
// club's own colours with its initials, so every tile reads as a badge.
function TeamCrest({ fill, stroke, ink, label }) {
  return (
    <svg className="team-crest" viewBox="0 0 40 46" aria-hidden="true" focusable="false">
      <path
        d="M20 3 L36 9 L36 23 C36 35 29 41 20 44 C11 41 4 35 4 23 L4 9 Z"
        fill={fill}
        stroke={stroke}
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <text
        x="20"
        y="27"
        textAnchor="middle"
        fontSize="12.5"
        fontWeight="700"
        fill={ink}
        style={{ fontFamily: 'var(--f-display)', letterSpacing: '.02em' }}
      >
        {label}
      </text>
    </svg>
  );
}

function ExpandIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M9 4H4v5M15 4h5v5M15 20h5v-5M9 20H4v-5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Club detail modal — the "identity card": big badge, heritage, grudges and
// honours. Deliberately no squad / finances / ratings (still the sell stage).
function TeamModal({ club, onClose, onPrev, onNext }) {
  const closeRef = useRef(null);

  // Lock scroll + focus the close button once, on open.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    if (closeRef.current) closeRef.current.focus();
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);

  // Keyboard: Esc closes, Left/Right step through clubs.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') onPrev();
      else if (e.key === 'ArrowRight') onNext();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, onPrev, onNext]);

  const cv = clubVars(club.primary_color);
  const honours = asHonours(club.honours);
  const meta = [TIER_NAMES[club.tier], club.town, club.founded ? `Est. ${club.founded}` : null]
    .filter(Boolean)
    .join(' · ');
  const groundLine = club.stadium
    ? `${club.stadium}${club.capacity ? `, ${Number(club.capacity).toLocaleString()}` : ''}`
    : null;
  const identity = IDENTITY_LINES[club.name]
    || [club.playing_style, club.owner_type].filter(Boolean).join(' · ');

  const onBackdrop = (e) => { if (e.target === e.currentTarget) onClose(); };

  return (
    <div className="team-modal-backdrop" onClick={onBackdrop} role="presentation">
      <button
        type="button"
        className="team-modal-nav prev"
        aria-label="Previous club"
        onClick={onPrev}
      >
        &#8249;
      </button>

      <div
        className="team-modal"
        role="dialog"
        aria-modal="true"
        aria-label={club.name}
        style={{ '--club': cv.border, '--club-glow': cv.glow, '--club-tint': cv.tint }}
      >
        <button
          type="button"
          className="team-modal-close"
          aria-label="Close"
          onClick={onClose}
          ref={closeRef}
        >
          &#215;
        </button>

        <div className="team-modal-content" key={club.id != null ? club.id : club.name}>
        <div className="team-modal-hd">
          <div className="team-modal-badge">
            {club.badge_url ? (
              <img src={club.badge_url} alt="" />
            ) : (
              <TeamCrest
                fill={club.primary_color || 'var(--bg-overlay)'}
                stroke={cv.border}
                ink={club.secondary_color || 'var(--tx-primary)'}
                label={initials(club.name)}
              />
            )}
          </div>
          <div className="team-modal-name">{club.name}</div>
          {club.nickname && <div className="team-modal-nick">{club.nickname}</div>}
          {meta && <div className="team-modal-meta">{meta}</div>}
        </div>

        <div className="team-modal-body">
          {club.motto && <p className="team-modal-motto">&ldquo;{club.motto}&rdquo;</p>}
          {club.description && <p className="team-modal-desc">{club.description}</p>}
          {identity && <p className="team-modal-identity">{identity}</p>}

          <div className="team-modal-facts">
            {groundLine && (
              <div className="tm-fact">
                <span className="tm-fact-k">Home</span>
                <span className="tm-fact-v">{groundLine}</span>
              </div>
            )}
            {club.rivals && (
              <div className="tm-fact">
                <span className="tm-fact-k">Rivals</span>
                <span className="tm-fact-v">{club.rivals}</span>
              </div>
            )}
            {club.record_signing && (
              <div className="tm-fact">
                <span className="tm-fact-k">Record signing</span>
                <span className="tm-fact-v">{club.record_signing}</span>
              </div>
            )}
          </div>

          {honours.length > 0 && (
            <div className="team-modal-honours">
              <div className="tm-fact-k">Honours</div>
              <ul>
                {honours.map((h) => <li key={h}>{h}</li>)}
              </ul>
            </div>
          )}
        </div>
        </div>
      </div>

      <button
        type="button"
        className="team-modal-nav next"
        aria-label="Next club"
        onClick={onNext}
      >
        &#8250;
      </button>
    </div>
  );
}

export default function ExploreTeams() {
  const [teams, setTeams] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | ready | empty | error
  const [activeTier, setActiveTier] = useState(1);
  const [active, setActive] = useState(0); // index into the tripled slide list
  const [modalIndex, setModalIndex] = useState(null); // index into `visible`, or null

  const railRef = useRef(null);
  const lenRef = useRef(0); // number of clubs in the current division
  const frameRef = useRef(0);
  const animatingRef = useRef(false); // true while an arrow-driven smooth scroll runs
  const animTimerRef = useRef(0);
  const dragRef = useRef({ down: false, startX: 0, startLeft: 0, moved: false, id: null });

  useEffect(() => {
    let alive = true;
    if (!dbSupabase) { setStatus('empty'); return undefined; }
    (async () => {
      const { data, error } = await dbSupabase
        .from('public_clubs')
        .select('*')
        .order('tier')
        .order('name');
      if (!alive) return;
      if (error) { setStatus('error'); return; }
      if (!data || data.length === 0) { setStatus('empty'); return; }
      setTeams(data);
      const firstTier = data.reduce((m, t) => Math.min(m, t.tier), Infinity);
      setActiveTier(Number.isFinite(firstTier) ? firstTier : 1);
      setStatus('ready');
    })();
    return () => { alive = false; };
  }, []);

  const presentTiers = TIER_ORDER.filter((tier) => teams.some((t) => t.tier === tier));
  const visible = teams.filter((t) => t.tier === activeTier);
  const len = visible.length;
  lenRef.current = len; // keep the loop math in sync with what's rendered

  const nearestIndex = useCallback(() => {
    const rail = railRef.current;
    if (!rail) return 0;
    const rr = rail.getBoundingClientRect();
    const mid = rr.left + rr.width / 2;
    const slides = rail.children;
    let best = 0;
    let bestDist = Infinity;
    for (let i = 0; i < slides.length; i += 1) {
      const r = slides[i].getBoundingClientRect();
      const c = r.left + r.width / 2;
      const d = Math.abs(c - mid);
      if (d < bestDist) { bestDist = d; best = i; }
    }
    return best;
  }, []);

  const recenter = useCallback(() => {
    const rail = railRef.current;
    const count = lenRef.current;
    if (!rail || !count) return;
    const slides = rail.children;
    if (slides.length < count * 2) return;
    const setWidth = slides[count].offsetLeft - slides[0].offsetLeft;
    if (setWidth <= 0) return;
    const idx = nearestIndex();
    if (idx < count) rail.scrollLeft += setWidth;
    else if (idx >= count * 2) rail.scrollLeft -= setWidth;
    setActive(nearestIndex());
  }, [nearestIndex]);

  const goTo = useCallback((index) => {
    const rail = railRef.current;
    if (!rail) return;
    const slides = rail.children;
    const i = Math.max(0, Math.min(index, slides.length - 1));
    const el = slides[i];
    if (!el) return;
    const target = el.offsetLeft + el.offsetWidth / 2 - rail.clientWidth / 2;
    const reduce = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    animatingRef.current = true;
    rail.scrollTo({ left: target, behavior: reduce ? 'auto' : 'smooth' });
    clearTimeout(animTimerRef.current);
    animTimerRef.current = setTimeout(() => {
      animatingRef.current = false;
      recenter();
    }, reduce ? 0 : 480);
  }, [recenter]);

  const stepBy = useCallback((dir) => {
    goTo(nearestIndex() + dir);
  }, [goTo, nearestIndex]);

  useEffect(() => {
    if (status !== 'ready') return undefined;
    const rail = railRef.current;
    if (!rail) return undefined;
    const onScroll = () => {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = requestAnimationFrame(() => {
        setActive(nearestIndex());
        if (!animatingRef.current && !dragRef.current.down) recenter();
      });
    };
    rail.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(frameRef.current);
      clearTimeout(animTimerRef.current);
      rail.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [status, nearestIndex, recenter]);

  // Park on the middle copy on first render and whenever the division changes.
  useEffect(() => {
    if (status !== 'ready') return undefined;
    const rail = railRef.current;
    if (!rail) return undefined;
    const raf = requestAnimationFrame(() => {
      const el = rail.children[lenRef.current];
      if (!el) return;
      rail.scrollLeft = el.offsetLeft + el.offsetWidth / 2 - rail.clientWidth / 2;
      setActive(lenRef.current);
    });
    return () => cancelAnimationFrame(raf);
  }, [status, activeTier]);

  const onPointerDown = (e) => {
    const rail = railRef.current;
    if (!rail) return;
    dragRef.current = {
      down: true, startX: e.clientX, startLeft: rail.scrollLeft, moved: false, id: e.pointerId,
    };
    rail.classList.add('is-dragging');
    if (rail.setPointerCapture) {
      try { rail.setPointerCapture(e.pointerId); } catch (err) { /* no-op */ }
    }
  };

  const onPointerMove = (e) => {
    const rail = railRef.current;
    const drag = dragRef.current;
    if (!rail || !drag.down) return;
    const dx = e.clientX - drag.startX;
    if (Math.abs(dx) > 4) drag.moved = true;
    rail.scrollLeft = drag.startLeft - dx;
  };

  // End of a pointer gesture. Because the rail uses pointer capture for
  // dragging (which redirects the native click to the rail), we resolve taps
  // here: a tap on the expand icon opens that club's modal, any other tap
  // centres the tile. `isTap` is only true for pointerup (not pointercancel).
  const endDrag = (e, isTap) => {
    const rail = railRef.current;
    const drag = dragRef.current;
    if (!rail || !drag.down) return;
    drag.down = false;
    rail.classList.remove('is-dragging');
    if (rail.releasePointerCapture && drag.id != null) {
      try { rail.releasePointerCapture(drag.id); } catch (err) { /* no-op */ }
    }
    drag.id = null;

    if (isTap && !drag.moved && e && typeof e.clientX === 'number') {
      const under = document.elementFromPoint(e.clientX, e.clientY);
      const expandBtn = under && under.closest ? under.closest('.team-expand') : null;
      const slide = under && under.closest ? under.closest('.team-slide') : null;
      if (slide && slide.parentNode === rail) {
        const idx = Array.prototype.indexOf.call(rail.children, slide);
        if (idx >= 0) {
          if (expandBtn && len) { setModalIndex(idx % len); return; }
          goTo(idx);
          return;
        }
      }
    }

    requestAnimationFrame(recenter);
  };

  const realActive = len ? ((active % len) + len) % len : 0;

  // Club detail modal navigation (loops within the current division).
  const modalClub = modalIndex != null && modalIndex < len ? visible[modalIndex] : null;
  const closeModal = () => setModalIndex(null);
  const modalPrev = () => setModalIndex((i) => (i == null || !len ? i : (i - 1 + len) % len));
  const modalNext = () => setModalIndex((i) => (i == null || !len ? i : (i + 1) % len));

  return (
    <section id="teams" style={{ borderTop: '1px solid var(--line)' }}>
      <div className="wrap">
        <div className="rev">
          <div className="sec-label">The teams</div>
          <h2>A world of your own.</h2>
          <p className="sec-intro">
            Every club, player and rivalry in Groundwork is fictional: a living, breathing football
            world with its own history, its own grudges and its own heroes. Forty-eight clubs across
            four divisions, from the Premier League to the bottom of League Two. Pick a division and
            explore:
          </p>
        </div>

        {status === 'ready' && presentTiers.length > 0 && (
          <div className="team-tabs" role="tablist" aria-label="Divisions">
            {presentTiers.map((tier) => (
              <button
                type="button"
                key={tier}
                role="tab"
                aria-selected={tier === activeTier}
                className={`team-tab${tier === activeTier ? ' is-active' : ''}`}
                onClick={() => setActiveTier(tier)}
              >
                {TIER_NAMES[tier] || `Tier ${tier}`}
              </button>
            ))}
          </div>
        )}

        {status === 'ready' && len > 0 && (
          <div className="team-controls">
            <div className="team-counter">
              <span className="team-counter-now">{pad2(realActive + 1)}</span>
              <span className="team-counter-sep">/</span>
              <span className="team-counter-all">{pad2(len)}</span>
            </div>
            <div className="team-arrows">
              <button
                type="button"
                className="team-arrow"
                aria-label="Previous team"
                onClick={() => stepBy(-1)}
              >
                &#8249;
              </button>
              <button
                type="button"
                className="team-arrow"
                aria-label="Next team"
                onClick={() => stepBy(1)}
              >
                &#8250;
              </button>
            </div>
          </div>
        )}
      </div>

      {status === 'loading' && (
        <div className="wrap"><p className="team-note">Loading the database…</p></div>
      )}

      {status === 'ready' && len > 0 && (
        <div
          className="team-rail"
          ref={railRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={(e) => endDrag(e, true)}
          onPointerCancel={(e) => endDrag(e, false)}
        >
          {Array.from({ length: len * COPIES }).map((_, k) => {
            const t = visible[k % len];
            const cv = clubVars(t.primary_color);
            const isClone = Math.floor(k / len) !== 1; // middle copy is the "real" one for a11y
            return (
              <div
                className={`team-slide${k === active ? ' is-focus' : ''}`}
                key={`${t.id}-${k}`}
                aria-hidden={isClone ? true : undefined}
                style={{ '--club': cv.border, '--club-glow': cv.glow }}
              >
                <div className="team-card">
                  <button
                    type="button"
                    className="team-expand"
                    aria-label={`More about ${t.name}`}
                    tabIndex={isClone ? -1 : 0}
                    onClick={() => setModalIndex(k % len)}
                  >
                    <ExpandIcon />
                  </button>
                  <div className="team-head">
                    <div className="team-badge">
                      {t.badge_url ? (
                        <img src={t.badge_url} alt="" loading="lazy" draggable="false" />
                      ) : (
                        <TeamCrest
                          fill={t.primary_color || 'var(--bg-overlay)'}
                          stroke={cv.border}
                          ink={t.secondary_color || 'var(--tx-primary)'}
                          label={initials(t.name)}
                        />
                      )}
                    </div>
                    <div className="team-id">
                      <div className="team-name">{t.name}</div>
                      <div className="team-town">
                        {t.town}{t.nickname ? ` · ${t.nickname}` : ''}
                      </div>
                    </div>
                  </div>
                  {t.description && <p className="team-desc">{t.description}</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {(status === 'empty' || status === 'error') && (
        <div className="wrap"><p className="team-note">Team profiles are on their way. Check back soon.</p></div>
      )}

      {modalClub && (
        <TeamModal
          club={modalClub}
          onClose={closeModal}
          onPrev={modalPrev}
          onNext={modalNext}
        />
      )}
    </section>
  );
}
